import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import Badge from '../../components/ui/Badge';
import { Button, ChevronLeftIcon } from '../../components/ui';
import { PencilIcon } from '../../components/ui/Icons';
import { CrewBackButton } from './CrewBackButton';
import { crewDisplayName } from './formMappers';
import { moduleConfig } from './config';
import type { CrewFile, CrewTransactionRow } from './types';

const crewInitials = (item: Pick<CrewFile, 'firstName' | 'lastName'>) => {
  const a = item.firstName?.trim()?.[0] ?? '';
  const b = item.lastName?.trim()?.[0] ?? '';
  return (a + b).toUpperCase() || '?';
};

const ageFromBirthDate = (birthDate?: string | null) => {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let years = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years -= 1;
  return years >= 0 ? years : null;
};

const daysUntil = (iso?: string | null) => {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
};

const getCurrentAssignment = (transactions?: CrewTransactionRow[]) => {
  const list = transactions ?? [];
  const onBoard = list.filter((t) => t.status === 'ONB');
  if (onBoard.length) {
    return onBoard.sort((a, b) => (b.signOnDate ?? '').localeCompare(a.signOnDate ?? ''))[0];
  }
  return list.sort((a, b) =>
    (b.signOnDate ?? b.startDate ?? '').localeCompare(a.signOnDate ?? a.startDate ?? ''),
  )[0];
};

export function CrewIdentityViewActions({ crewId }: { crewId: number }) {
  return (
    <Link
      to={moduleConfig.module.route.edit(crewId)}
      state={{ from: moduleConfig.module.route.detail(crewId) }}
    >
      <Button type="button" variant="accent" size="sm">
        <PencilIcon className="h-4 w-4" />
        Edit
      </Button>
    </Link>
  );
}

export type CrewIdentityHeaderProps = {
  item: CrewFile;
  /** Sit inside an existing card (form / full file) instead of its own rounded card. */
  embedded?: boolean;
  /** Override back navigation (create/edit use an explicit return path). */
  onBack?: () => void;
  backTitle?: string;
  fallbackPath?: string;
  /** Override the display name (e.g. “New crew file”). */
  title?: string;
  actions?: React.ReactNode;
  className?: string;
};

export const CrewIdentityHeader: React.FC<CrewIdentityHeaderProps> = ({
  item,
  embedded = false,
  onBack,
  backTitle = 'Back',
  fallbackPath,
  title,
  actions,
  className,
}) => {
  const { colors } = useTheme();

  const current = useMemo(() => getCurrentAssignment(item.transactions), [item.transactions]);
  const onBoard = current?.status === 'ONB';
  const certStats = useMemo(() => {
    const certs = item.certs ?? [];
    const expiring = certs.filter((c) => {
      const d = daysUntil(c.expiredDate);
      return d != null && d >= 0 && d <= 90;
    }).length;
    const expired = certs.filter((c) => {
      const d = daysUntil(c.expiredDate);
      return d != null && d < 0;
    }).length;
    return { expiring, expired };
  }, [item.certs]);

  const age = ageFromBirthDate(item.birthDate);
  const displayName = title ?? crewDisplayName(item);
  const isExisting = item.id > 0;

  const body = (
    <div className="flex items-center gap-2.5 px-3 py-2.5 sm:gap-3 sm:px-4">
      {onBack ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={backTitle}
          aria-label={backTitle}
          className="shrink-0"
          onClick={onBack}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </Button>
      ) : (
        <CrewBackButton className="shrink-0 self-center" fallbackPath={fallbackPath} title={backTitle} />
      )}
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold',
          colors.surfaceHover,
          colors.text,
          'ring-1 ring-inset',
          colors.border,
        )}
        aria-hidden
      >
        {crewInitials(item)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h2 className={cn('truncate text-base font-semibold sm:text-lg', colors.text)}>
                {displayName}
              </h2>
              <p className={cn('truncate text-xs sm:text-sm', colors.textMuted)}>
                {[
                  item.rank?.name || item.rank?.abrv,
                  item.nationality?.name,
                  age != null ? `Age ${age}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') ||
                  (isExisting || item.firstName?.trim() || item.lastName?.trim()
                    ? 'Rank not set'
                    : 'Create a crew file')}
              </p>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {item.crewRef ? (
                <span
                  className={cn(
                    'inline-flex rounded-md px-1.5 py-0.5 font-mono text-[11px]',
                    colors.surfaceHover,
                    colors.textSecondary,
                  )}
                >
                  {item.crewRef}
                </span>
              ) : null}
              {item.availabilityStatus?.name ? (
                <Badge variant="info">{item.availabilityStatus.name}</Badge>
              ) : null}
              {isExisting ? (
                <Badge variant={onBoard ? 'success' : 'secondary'}>
                  {onBoard ? 'On board' : 'Off board'}
                </Badge>
              ) : null}
              {item.notRecommended ? <Badge variant="danger">Not recommended</Badge> : null}
              {certStats.expired > 0 ? (
                <Badge variant="danger">{certStats.expired} cert expired</Badge>
              ) : null}
              {certStats.expired === 0 && certStats.expiring > 0 ? (
                <Badge variant="warning">{certStats.expiring} cert ≤90d</Badge>
              ) : null}
              {(item.mobile1 || item.email1 || item.telephone1) && (
                <span className={cn('text-xs', colors.textSecondary)}>
                  {[item.mobile1 || item.telephone1, item.email1].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div> : null}
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return <div className={cn('flex-shrink-0 border-b', colors.border, className)}>{body}</div>;
  }

  return (
    <section className={cn('rounded-xl border shadow-sm overflow-hidden', colors.surface, colors.border, className)}>
      {body}
    </section>
  );
};

export default CrewIdentityHeader;
